import { useEffect, useMemo, useRef, useState } from "react";
import type { ModuleManifest } from "@praesto/contracts";
import type { User } from "@praesto/shared-types";
import { RouterProvider } from "@tanstack/react-router";
import {
  registerModulePermissions,
  setPersonResolver,
  setRbacGlobalRole,
  setRbacMemberships,
  setSuperadminApi,
  setUsersApi,
} from "@praesto/core";
import { computeBootState, parseTenantSlug } from "./boot";
import { recoverTenantFromAuth } from "./lib/tenant-recovery";
import { ActiveHotelProvider, CoreProvider, getCore, useCore } from "./lib/core";
import { sendPasswordReset, signIn } from "./lib/auth-actions";
import { decodeIdTokenClaims } from "./lib/jwt";
import { getEnv } from "./lib/env";
import { loadActiveModules } from "./module-loader";
import {
  browserRecoveryDeps,
  recoverFromBootError,
  updateServiceWorker,
} from "./lib/boot-recovery";
import { BootGate } from "./components/BootGate";
import { HubView } from "./components/HubView";
import { LoginView } from "./components/LoginView";
import { MovedNotice } from "./components/MovedNotice";
import { ResetPasswordView } from "./components/ResetPasswordView";
import { EmailActionErrorView, EmailActionView } from "./components/EmailActionView";
import { isAuthActionPath, parseEmailAction } from "./lib/email-action";
import { SignupPage } from "./signup/SignupPage";
import { buildMademindRouter } from "./mademind/router-mademind";
import { buildRouter } from "./router";
import { ManifestsProvider } from "./lib/manifests-context";
import { movedTarget } from "./lib/moved-host";

/**
 * Boot orchestrator (apps/web CLAUDE.md "Boot flow"):
 * tenantSlug → CoreAPI → auth → tenant settings → loadActiveModules →
 * shell/layout. Pure transitions live in boot.ts; this only wires the
 * live CoreAPI observers into them.
 */
function Boot({ tenantSlug }: { tenantSlug: string }) {
  const core = useCore();
  const [authResolved, setAuthResolved] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [manifests, setManifests] = useState<ReadonlyArray<ModuleManifest> | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);
  const [rbacReady, setRbacReady] = useState(false);
  const loadingModules = useRef(false);

  useEffect(
    () =>
      core.auth.onAuthChange((u) => {
        setUser(u);
        setAuthResolved(true);
        if (!u) {
          setRbacReady(false);
          return;
        }
        // After a successful login the browser URL is still
        // `/$tenantSlug/login` (the login overlay renders over it without
        // navigating). The router has no `/login` child route, so mounting on
        // that URL shows "Not Found". Normalize to the home landing before the
        // router mounts.
        const path = window.location.pathname;
        const slug = path.split("/")[1];
        if (slug && /\/login\/?$/.test(path)) {
          window.history.replaceState(null, "", `/${slug}/home`);
        }
        // Block boot until both memberships + globalRole claim are
        // pushed into the RBAC singleton — otherwise SubNavSidebar
        // and other RBAC-gated UI renders empty on first paint.
        void Promise.allSettled([
          core.organization.membershipsForUser(u.id).then((ms) => {
            setRbacMemberships(core.rbac, ms);
          }),
          core.auth.getIdToken(true).then((tok) => {
            const claims = decodeIdTokenClaims<{ globalRole?: string }>(tok);
            if (claims) setRbacGlobalRole(core.rbac, claims.globalRole as never);
          }),
        ]).then(() => setRbacReady(true));
      }),
    [core],
  );

  useEffect(() => {
    if (!user) return;
    return core.tenant.onSettingsChange(() => {
      if (loadingModules.current || manifests) return;
      loadingModules.current = true;
      loadActiveModules(core.tenant.activeModules(), {
        // The config knows a module this bundle does not: the bundle is stale.
        // Boot without it and fetch the new version for the next opening.
        onUnknownModule: () => void updateServiceWorker(browserRecoveryDeps().serviceWorker),
      })
        .then((ms) => {
          for (const m of ms) {
            core.i18n.registerModuleTranslations(m.id, m.translations);
            registerModulePermissions(core.rbac, m.permissions);
            void m.lifecycle.onInit?.({
              core,
              moduleId: m.id,
              logger: console,
            });
          }
          setManifests(ms);
        })
        .catch((e: unknown) => {
          // The technical message goes to the console; the user sees the
          // boot-error screen (BootGate) while one automatic reload is tried.
          console.error("[boot] module load failed", e);
          setError(e instanceof Error ? e.message : "module load failed");
          void recoverFromBootError(browserRecoveryDeps());
        });
    });
  }, [core, user, manifests]);

  const state = computeBootState({
    tenantSlug,
    authResolved,
    user: user ? { id: user.id } : null,
    settingsLoaded: manifests !== null,
    modulesLoaded: manifests !== null,
    rbacReady,
    ...(error ? { error } : {}),
  });

  return (
    <BootGate
      state={state}
      login={
        <LoginView
          tenantSlug={tenantSlug}
          onSignIn={(email, password) => signIn(tenantSlug, email, password)}
          onReset={(email) =>
            sendPasswordReset(tenantSlug, email, `${window.location.origin}/${tenantSlug}/login`)
          }
        />
      }
    >
      {manifests ? <RoutedApp manifests={manifests} /> : null}
    </BootGate>
  );
}

/**
 * Build the TanStack Router instance once manifests are loaded.
 * Memoised so route table is stable across re-renders.
 */
function RoutedApp({ manifests }: { manifests: ReadonlyArray<ModuleManifest> }) {
  const router = useMemo(() => buildRouter(manifests), [manifests]);
  return (
    <ManifestsProvider manifests={manifests}>
      <RouterProvider router={router} />
    </ManifestsProvider>
  );
}

/**
 * Mademind-specific boot: no module loader, no tenant-settings subscribe.
 * The superadmin console is host-only (Tenants / Operators / Audit).
 * Reuses LoginView (Identity Platform tenant `mademind-*`) and only sets
 * the globalRole into the RBAC.
 */
function MademindBoot({ tenantSlug }: { tenantSlug: string }) {
  const core = useCore();
  const [authResolved, setAuthResolved] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [rbacReady, setRbacReady] = useState(false);
  const [globalRole, setGlobalRole] = useState<string | undefined>(undefined);

  useEffect(
    () =>
      core.auth.onAuthChange((u) => {
        setUser(u);
        setAuthResolved(true);
        if (!u) {
          setRbacReady(false);
          setGlobalRole(undefined);
          return;
        }
        void core.auth
          .getIdToken(true)
          .then((tok) => {
            const claims = decodeIdTokenClaims<{ globalRole?: string }>(tok);
            setGlobalRole(claims?.globalRole);
            if (claims) setRbacGlobalRole(core.rbac, claims.globalRole as never);
          })
          .finally(() => setRbacReady(true));
      }),
    [core],
  );

  const state = computeBootState({
    tenantSlug,
    authResolved,
    user: user ? { id: user.id } : null,
    settingsLoaded: true,
    modulesLoaded: true,
    rbacReady,
  });

  // Second layer of defense (mirrors the backend gate
  // `globalRole === "superadmin"` in superadmin/index.ts): even for the exact
  // console tenant, only an actual superadmin sees the console — a non-superadmin
  // authenticated in this tenant gets access-denied, never the console shell.
  const isSuperadmin = globalRole === "superadmin";

  return (
    <BootGate
      state={state}
      login={
        <LoginView
          tenantSlug={tenantSlug}
          onSignIn={(email, password) => signIn(tenantSlug, email, password)}
          onReset={(email) =>
            sendPasswordReset(tenantSlug, email, `${window.location.origin}/${tenantSlug}/login`)
          }
        />
      }
    >
      {isSuperadmin ? <MademindConsole /> : <SuperadminAccessDenied />}
    </BootGate>
  );
}

/**
 * The console behind its own router, built once per mount.
 *
 * Separate from the tenant app's router: the console has no module manifests
 * and its own navigation shape. Building it inside a component (rather than at
 * module scope) keeps the router from being created for every visitor of the
 * tenant app, which never mounts this branch.
 */
function MademindConsole() {
  const [router] = useState(() => buildMademindRouter());
  return <RouterProvider router={router as never} />;
}

/** Shown when a non-superadmin is authenticated in the console tenant. */
function SuperadminAccessDenied() {
  const core = useCore();
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
      <p className="text-mono-xs uppercase tracking-widest text-muted">Praesto Console</p>
      <h1 className="text-h4 font-semibold">Accesso negato</h1>
      <p className="max-w-sm text-sm text-muted">
        Il tuo account non ha i permessi per accedere alla Console. Se pensi sia un errore, contatta
        un amministratore.
      </p>
      <button
        type="button"
        className="mt-2 text-sm underline"
        onClick={() => void core.auth.signOut()}
      >
        Esci
      </button>
    </div>
  );
}

/**
 * No tenant in the path (installed PWA at "/", or arriving from the website's
 * Accedi, or a magic-link click). Recover the tenant from the persisted
 * session and redirect; while that runs show a neutral "recognizing" state
 * (never the dead-end); if there is no session (or a magic-link token is
 * present, see App()), render the Access Hub.
 */
function RootAccessGate() {
  const hasHubToken = new URLSearchParams(window.location.search).has("hubToken");
  const [state, setState] = useState<"recognizing" | "hub">(hasHubToken ? "hub" : "recognizing");
  useEffect(() => {
    if (hasHubToken) return; // magic-link always wins over an existing session
    let cancelled = false;
    void recoverTenantFromAuth().then((tenant) => {
      if (cancelled) return;
      if (tenant) window.location.replace(`/${tenant}/home`);
      else setState("hub");
    });
    return () => {
      cancelled = true;
    };
  }, [hasHubToken]);
  if (state === "recognizing")
    return (
      <BootGate state={{ phase: "recognizing" }} login={null}>
        {null}
      </BootGate>
    );
  const params = new URLSearchParams(window.location.search);
  const linkToken = params.get("hubToken") ?? undefined;
  const email = params.get("e") ?? undefined;
  return (
    <HubView
      {...(linkToken ? { initialLinkToken: linkToken } : {})}
      {...(email ? { initialEmail: email } : {})}
    />
  );
}

/**
 * The explicit Identity Platform action route, for every mode. Dispatched before
 * anything reads the path as a tenant slug: here the first segment is `auth`,
 * and the only tenant is the link's `tenantId` (the IP tenant id IS the slug).
 */
function AuthActionRoute() {
  const action = parseEmailAction(window.location.search);
  if (action.kind === "invalid") return <EmailActionErrorView reason={action.reason} />;
  if (action.kind === "applyCode") {
    return (
      <EmailActionView mode={action.mode} oobCode={action.oobCode} tenantId={action.tenantId} />
    );
  }
  const view = (
    <ResetPasswordView
      tenantId={action.tenantId ?? ""}
      tenantSlug={action.tenantId ?? ""}
      oobCode={action.oobCode}
    />
  );
  // Same wiring as the legacy intercept below when the link names a tenant; a
  // link without one never boots a CoreAPI (auth-actions finds-or-inits the app).
  return action.tenantId ? (
    <CoreProvider value={getCore(action.tenantId)}>{view}</CoreProvider>
  ) : (
    view
  );
}

export function App() {
  // On a host the app moved away from, nothing else starts: not the tenant
  // boot, not the reset intercept, no `/api` call. Only the notice with the
  // way to the new address (lib/moved-host.ts).
  const moved = movedTarget(window.location);
  if (moved) return <MovedNotice target={moved} />;

  if (isAuthActionPath(window.location.pathname)) return <AuthActionRoute />;

  const tenantSlug = parseTenantSlug(window.location.pathname);

  // Legacy intercept, kept for reset links already sent (any path, e.g.
  // `/{tenant}/?mode=resetPassword…` from `buildInAppResetLink`). Intercept
  // Identity Platform email-action links (password reset) BEFORE the
  // normal boot/login flow — the user is locked out, so this must render without
  // auth. The IP action URL must point at this app (any path); we key off the
  // query params. `tenantId` is appended by IP for multi-tenant projects.
  const actionParams = new URLSearchParams(window.location.search);
  if (actionParams.get("mode") === "resetPassword" && actionParams.get("oobCode")) {
    const oobCode = actionParams.get("oobCode")!;
    // IP appends `tenantId` to the email-action link, and in this app the tenant
    // slug IS the Identity Platform tenant id. Prefer it over the path so the
    // action URL can be a tenant-agnostic `/__/auth/action` and "Vai al login"
    // still targets the right tenant (the path segment there is `__`, not a slug).
    const tenantId = actionParams.get("tenantId") ?? tenantSlug ?? "";
    const slug = tenantId || tenantSlug || "";
    return (
      <CoreProvider value={getCore(slug)}>
        <ResetPasswordView tenantId={tenantId} tenantSlug={slug} oobCode={oobCode} />
      </CoreProvider>
    );
  }

  // Public self-service signup — there is no tenant yet, so this must be
  // checked before `tenantSlug` (the first path segment) is treated as a
  // real tenant slug to boot against; otherwise `/signup` would be misread
  // as tenantSlug "signup" and fed into `getCore()`.
  //
  // NOTE on placement: the plan for this task said to register `/signup` in
  // `router.tsx` as "a sibling of the Access Hub `/` route". In practice the
  // Access Hub (`RootAccessGate`/`HubView` below) is NOT a `router.tsx`
  // route either — `buildRouter()` only ever runs post-auth, once tenant
  // manifests are loaded (see `RoutedApp`), so nothing in `router.tsx` is
  // ever mounted for an unauthenticated, pre-tenant visitor. The real
  // "sibling of the Access Hub" is this early dispatch in `App()`, so
  // `/signup` (like the reset-password intercept above and `RootAccessGate`
  // below) is wired here instead of adding an unreachable route to
  // `router.tsx`.
  if (window.location.pathname === "/signup" || window.location.pathname === "/signup/") {
    return <SignupPage />;
  }

  // A magic-link click must always land on the Hub's verify step, even if a
  // session for a different (or the same) tenant already exists.
  const hasHubToken = new URLSearchParams(window.location.search).has("hubToken");
  if (!tenantSlug || hasHubToken) {
    return <RootAccessGate />;
  }

  const core = getCore(tenantSlug);
  // Wire the host-level API singletons: tenant-scoped CRUD goes through
  // the Cloud Functions with the Firebase ID token as Bearer. Empty
  // apiBase → same origin (Hosting → Functions rewrite).
  setUsersApi({
    apiBase: "",
    getAuthToken: () => core.auth.getIdToken(),
  });
  // Wire PersonLink resolver so any module can lazily resolve display names
  // via @praesto/core without knowing about the users API directly.
  // core.organization.users() now has a built-in 30s TTL cache, so
  // concurrent PersonLink resolutions (one per unique uid on a busy
  // grid) share one Firestore read without extra plumbing here.
  setPersonResolver(async (uid) => {
    const users = (await core.organization.users()) as ReadonlyArray<{
      id: string;
      displayName?: string;
      email: string;
      photoUrl?: string;
    }>;
    const u = users.find((x) => x.id === uid);
    if (!u) return undefined;
    return {
      displayName: u.displayName ?? u.email,
      ...(u.photoUrl ? { photoUrl: u.photoUrl } : {}),
    };
  });
  setSuperadminApi({
    apiBase: "",
    getAuthToken: () => core.auth.getIdToken(),
  });

  // Route to the superadmin console ONLY for the exact configured console tenant
  // (mirrors the backend's `MADEMIND_TENANT_SLUG` check) — never a `mademind-`
  // slug-prefix match, which a self-service tenant named "Mademind" could enter.
  // Fail-closed: no configured id → never the console.
  const mademindSlug = getEnv().mademindTenantSlug;
  const isMademind = mademindSlug !== undefined && tenantSlug === mademindSlug;

  return (
    <CoreProvider value={core}>
      {isMademind ? (
        <MademindBoot tenantSlug={tenantSlug} />
      ) : (
        <ActiveHotelProvider>
          <Boot tenantSlug={tenantSlug} />
        </ActiveHotelProvider>
      )}
    </CoreProvider>
  );
}
